import React, { useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Badge, Tooltip } from "@material-ui/core";
import { Filter as FilterIcon, X as CloseIcon } from "lucide-react";
import ChatiaPopover from "../ChatiaPopover";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const colors = ds.colors || {};
  const radius = ds.radius || { sm: 10, md: 12 };
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    triggerBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 14px",
      borderRadius: radius.md,
      border: "1px solid transparent",
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: isDark
        ? "0 2px 8px rgba(0,0,0,0.35)"
        : "0 2px 8px rgba(0,0,0,0.08)",
      transition: "filter 0.15s, box-shadow 0.15s",
      "&:hover": {
        filter: "brightness(1.05)",
        boxShadow: isDark
          ? "0 3px 12px rgba(0,0,0,0.45)"
          : "0 3px 12px rgba(0,0,0,0.12)",
      },
    },
    triggerActive: {
      filter: "brightness(1.1)",
    },
    fields: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      padding: 12,
      minWidth: 320,
      // padroniza inputs/selects internos no popover de filtros
      "& .MuiBox-root": {
        padding: "0 !important",
      },
      "& > div > div, & .MuiFormControl-root": {
        width: "100% !important",
        marginTop: 0,
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: `${radius.md}px !important`,
        fontSize: 13,
      },
      "& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
        paddingTop: 4,
        paddingBottom: 4,
      },
      "& .MuiAutocomplete-tag": {
        borderRadius: 50,
        fontWeight: 600,
        fontSize: "0.78em",
        height: 22,
      },
      "& .MuiInputLabel-outlined": {
        fontSize: 13,
      },
      "& input[type='date']": {
        fontFamily: "inherit",
      },
      "& input[type='date']::-webkit-calendar-picker-indicator": {
        filter: isDark ? "invert(0.85)" : "none",
        cursor: "pointer",
        opacity: 0.7,
        padding: 2,
        borderRadius: 4,
      },
    },
    fieldRow: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    fieldLabel: {
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: colors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    footer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 12px",
      borderTop: `1px solid ${colors.border || (isDark ? "#2a3942" : "#eee")}`,
    },
    clearBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "4px 10px",
      borderRadius: radius.sm,
      border: "none",
      background: "transparent",
      color: "#e53935",
      fontSize: 12,
      cursor: "pointer",
      "&:hover": { background: "rgba(255, 255, 255, 0.06)" },
    },
  };
});

const Field = ({ label, children }) => {
  const classes = useStyles();
  return (
    <div className={classes.fieldRow}>
      {label && <span className={classes.fieldLabel}>{label}</span>}
      {children}
    </div>
  );
};

const FilterButton = ({ label, activeCount = 0, onClear, children, ...rest }) => {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title={label || ""}>
        <button
          ref={anchorRef}
          type="button"
          className={`${classes.triggerBtn} ${activeCount > 0 ? classes.triggerActive : ""}`}
          onClick={() => setOpen((p) => !p)}
        >
          <Badge color="primary" badgeContent={activeCount > 0 ? activeCount : null} variant="dot" overlap="rectangular">
            <FilterIcon size={16} strokeWidth={1.5} />
          </Badge>
          <span>{label}</span>
        </button>
      </Tooltip>
      <ChatiaPopover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        title={label}
        paperStyle={{ minWidth: 340 }}
        {...rest}
      >
        <div className={classes.fields}>{children}</div>
        {onClear && (
          <div className={classes.footer}>
            <button
              type="button"
              className={classes.clearBtn}
              onClick={() => {
                onClear();
                setOpen(false);
              }}
            >
              <CloseIcon size={12} /> Limpar filtros
            </button>
          </div>
        )}
      </ChatiaPopover>
    </>
  );
};

FilterButton.Field = Field;

export default FilterButton;
